// frontend/src/pages/manufacturing/v2/components/OrderTimelineV2.jsx
//
// Collapsible "Order Timeline" card for the customer order detail page.
// Lazily fetches GET /api/v2/customer-orders/{id}/timeline/ on first expand and
// renders a light, hand-rolled vertical feed (no heavy MUI Timeline lib).
//
// Contract (#3): { events: [{ at, kind, title, detail, user, line_id, wo_number }] }
// ascending. kind ∈ order_created, status_change, line_exploded, remake,
// step_completed, scrap, allocation, shipment, unsend, tree_deleted,
// order_completed.
//
// Design rules (mirrors OrderV2ProductionSection): never break the host V1 page.
// Errors fail quietly with a small retry link.

import React, { useCallback, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Collapse, Link, LinearProgress, Chip,
  Avatar, Tooltip,
} from '@mui/material';
import {
  ExpandMore, ExpandLess, History, ShoppingCart, Factory, Autorenew,
  CheckCircle, Warning, Inventory, LocalShipping, Undo, DeleteForever,
  SwapHoriz, Flag, Person, Circle,
} from '@mui/icons-material';

import { v2 } from '../../../../services/api';

// Icon + colour per event kind. Falls back to a neutral dot.
const KIND_META = {
  order_created: { Icon: ShoppingCart, color: 'primary' },
  status_change: { Icon: SwapHoriz, color: 'info' },
  line_exploded: { Icon: Factory, color: 'secondary' },
  remake: { Icon: Autorenew, color: 'secondary' },
  step_completed: { Icon: CheckCircle, color: 'success' },
  scrap: { Icon: Warning, color: 'error' },
  allocation: { Icon: Inventory, color: 'info' },
  shipment: { Icon: LocalShipping, color: 'primary' },
  unsend: { Icon: Undo, color: 'warning' },
  tree_deleted: { Icon: DeleteForever, color: 'error' },
  order_completed: { Icon: Flag, color: 'success' },
};

function metaFor(kind) {
  return KIND_META[kind] || { Icon: Circle, color: 'default' };
}

// "3 days ago" style relative label. Keeps it dependency-free (date-fns is a
// dep but this is trivial and avoids locale surprises in tests).
function relativeTime(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  const hours = Math.round(abs / 3600000);
  const days = Math.round(abs / 86400000);
  const suffix = diffMs >= 0 ? 'ago' : 'from now';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ${suffix}`;
  if (hours < 24) return `${hours} hr ${suffix}`;
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ${suffix}`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} mo ${suffix}`;
  return `${Math.round(days / 365)} yr ${suffix}`;
}

function absoluteTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso || '');
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function TimelineRow({ event, isLast }) {
  const { Icon, color } = metaFor(event.kind);
  return (
    <Box
      sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}
      data-testid={`timeline-event-${event.kind}`}
    >
      {/* Rail: icon + connector line */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar
          sx={{ width: 32, height: 32, bgcolor: `${color}.light`, color: `${color}.contrastText` }}
          variant="circular"
        >
          <Icon fontSize="small" />
        </Avatar>
        {!isLast && (
          <Box sx={{ flexGrow: 1, width: '2px', bgcolor: 'divider', my: 0.5, minHeight: 16 }} />
        )}
      </Box>

      {/* Body */}
      <Box sx={{ pb: isLast ? 0 : 2, flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight="bold" component="span">
              {event.title}
              {event.wo_number ? (
                <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.75 }}>
                  {event.wo_number}
                </Typography>
              ) : null}
            </Typography>
            {event.detail && (
              <Typography variant="body2" color="text.secondary">
                {event.detail}
              </Typography>
            )}
            {event.user && (
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<Person fontSize="small" />}
                  label={event.user}
                  data-testid="timeline-user-chip"
                />
              </Box>
            )}
          </Box>
          <Tooltip title={absoluteTime(event.at)}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}
            >
              {relativeTime(event.at)}
              <Box component="span" sx={{ display: 'block' }}>{absoluteTime(event.at)}</Box>
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

export default function OrderTimelineV2({ orderId }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null); // null = not fetched yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Guard the once-only lazy fetch so a state update doesn't self-cancel it.
  const fetchedRef = useRef(false);

  const load = useCallback(async () => {
    if (orderId == null) return;
    setLoading(true);
    setError(false);
    try {
      const res = await v2.customerOrders.timeline(orderId);
      const data = res.data || {};
      const rows = Array.isArray(data) ? data : (data.events || []);
      setEvents(rows);
      fetchedRef.current = true;
    } catch (err) {
      console.error('Failed to load V2 order timeline for order', orderId, err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !fetchedRef.current) load();
  };

  return (
    <Card sx={{ mt: 3 }} data-testid="order-timeline-v2">
      <CardContent>
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={handleToggle}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, width: '100%',
            justifyContent: 'space-between', color: 'text.primary',
          }}
          data-testid="timeline-toggle"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History fontSize="small" color="action" />
            <Typography variant="h6">Order Timeline</Typography>
          </Box>
          {open ? <ExpandLess /> : <ExpandMore />}
        </Link>

        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 2 }}>
            {loading && <LinearProgress data-testid="timeline-loading" />}

            {error && !loading && (
              <Typography variant="caption" color="text.secondary">
                Couldn&apos;t load the timeline.{' '}
                <Link component="button" type="button" onClick={load} data-testid="timeline-retry">
                  Retry
                </Link>
              </Typography>
            )}

            {!loading && !error && events && events.length === 0 && (
              <Typography variant="body2" color="text.secondary" data-testid="timeline-empty">
                No events yet.
              </Typography>
            )}

            {!loading && !error && events && events.length > 0 && (
              <Box data-testid="timeline-feed">
                {events.map((event, idx) => (
                  <TimelineRow
                    // Events have no stable id in the contract; index within the
                    // ascending, append-only feed is stable enough for keys.
                    key={`${event.at}-${event.kind}-${idx}`}
                    event={event}
                    isLast={idx === events.length - 1}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}
